/**
 * Activities: the kinds of activity a school runs, the life of a student's
 * participation, and an eligibility engine that is rule-based and always
 * explains itself.
 *
 * One platform, activity-specific workflows. A varsity sport, a student
 * council and a marching band share registration, rosters, requirements and
 * eligibility, but each kind brings its own defaults: whether there are
 * tryouts, a physical, a jersey number, results, or none of those. Nothing
 * here assumes every activity is a sport.
 *
 * Eligibility is evaluated from facts the rest of Cornerstone owns (grades,
 * attendance, the Health Office's physical status, Discipline's activity
 * restriction, Forms, Student Accounts) against a named rule set. The result
 * is a status plus the exact conditions that failed. Each reason carries two
 * texts: what a coach may see ("Academic: below the passing-course
 * minimum") and what an administrator with grade access may see ("Algebra
 * II: F — 4 of 5 required passing courses"). Health and discipline reasons
 * only ever carry the category.
 */

import type { BaseDocument } from '../platform/documents.js'
import type { ActivityType } from './activities.js'

// ── Kinds ────────────────────────────────────────────────────────────────────

export type ActivityCategory = 'athletics' | 'clubs' | 'arts' | 'academic' | 'service' | 'other'

export interface ActivityKindDefinition {
  key: string
  label: string
  category: ActivityCategory
  /** The legacy `type` an activity of this kind carries, for older screens. */
  legacyType: ActivityType
  /** Defaults a new activity of this kind starts with; every one is editable. */
  defaults: {
    tryouts: boolean
    physical: boolean
    competitive: boolean
    /** Sports and band track a number; clubs never do. */
    numbered: boolean
    /** What the roster's role column is called. */
    roleLabel: string
    ruleSetKey: string
    /** Sports run in seasons; most clubs simply run all year. */
    seasonal: boolean
  }
}

const sport = (key: string, label: string): ActivityKindDefinition => ({
  key,
  label,
  category: 'athletics',
  legacyType: 'athletics',
  defaults: { tryouts: true, physical: true, competitive: true, numbered: true, roleLabel: 'Position', ruleSetKey: 'hs-athletics', seasonal: true },
})

export const ACTIVITY_KINDS: readonly ActivityKindDefinition[] = [
  sport('varsity_sport', 'Varsity sport'),
  sport('jv_sport', 'Junior varsity sport'),
  sport('freshman_sport', 'Freshman sport'),
  { ...sport('club_sport', 'Club sport'), defaults: { ...sport('x', 'x').defaults, tryouts: false, ruleSetKey: 'club-sport' } },
  {
    key: 'cheer_dance',
    label: 'Cheer / dance',
    category: 'athletics',
    legacyType: 'athletics',
    defaults: { tryouts: true, physical: true, competitive: true, numbered: false, roleLabel: 'Squad role', ruleSetKey: 'hs-athletics', seasonal: true },
  },
  {
    key: 'intramural',
    label: 'Intramural',
    category: 'athletics',
    legacyType: 'athletics',
    defaults: { tryouts: false, physical: true, competitive: false, numbered: false, roleLabel: 'Team', ruleSetKey: 'participation', seasonal: true },
  },
  {
    key: 'esports',
    label: 'Esports',
    category: 'athletics',
    legacyType: 'club',
    defaults: { tryouts: true, physical: false, competitive: true, numbered: false, roleLabel: 'Role', ruleSetKey: 'academic-only', seasonal: true },
  },
  {
    key: 'marching_band',
    label: 'Marching band',
    category: 'arts',
    legacyType: 'performing_arts',
    defaults: { tryouts: false, physical: false, competitive: true, numbered: true, roleLabel: 'Section', ruleSetKey: 'academic-only', seasonal: false },
  },
  {
    key: 'performing_arts',
    label: 'Performing arts',
    category: 'arts',
    legacyType: 'performing_arts',
    defaults: { tryouts: true, physical: false, competitive: false, numbered: false, roleLabel: 'Part', ruleSetKey: 'participation', seasonal: false },
  },
  {
    key: 'academic_competition',
    label: 'Academic competition',
    category: 'academic',
    legacyType: 'academic_team',
    defaults: { tryouts: false, physical: false, competitive: true, numbered: false, roleLabel: 'Competition team', ruleSetKey: 'academic-only', seasonal: false },
  },
  {
    key: 'academic_club',
    label: 'Academic club',
    category: 'academic',
    legacyType: 'club',
    defaults: { tryouts: false, physical: false, competitive: false, numbered: false, roleLabel: 'Role', ruleSetKey: 'participation', seasonal: false },
  },
  {
    key: 'honor_society',
    label: 'Honor society',
    category: 'academic',
    legacyType: 'student_organization',
    defaults: { tryouts: false, physical: false, competitive: false, numbered: false, roleLabel: 'Office', ruleSetKey: 'honor-society', seasonal: false },
  },
  {
    key: 'student_government',
    label: 'Student government',
    category: 'clubs',
    legacyType: 'student_organization',
    defaults: { tryouts: false, physical: false, competitive: false, numbered: false, roleLabel: 'Office', ruleSetKey: 'participation', seasonal: false },
  },
  {
    key: 'student_organization',
    label: 'Student organization',
    category: 'clubs',
    legacyType: 'student_organization',
    defaults: { tryouts: false, physical: false, competitive: false, numbered: false, roleLabel: 'Role', ruleSetKey: 'participation', seasonal: false },
  },
  {
    key: 'service',
    label: 'Volunteer / service',
    category: 'service',
    legacyType: 'student_organization',
    defaults: { tryouts: false, physical: false, competitive: false, numbered: false, roleLabel: 'Role', ruleSetKey: 'participation', seasonal: false },
  },
  {
    key: 'other',
    label: 'Other',
    category: 'other',
    legacyType: 'club',
    defaults: { tryouts: false, physical: false, competitive: false, numbered: false, roleLabel: 'Role', ruleSetKey: 'participation', seasonal: false },
  },
]

export const activityKind = (key: string | null | undefined): ActivityKindDefinition =>
  ACTIVITY_KINDS.find((kind) => kind.key === key) ?? ACTIVITY_KINDS.find((kind) => kind.key === 'other')!

/** The kind an older activity (type only) is read as. */
export function kindForLegacyType(type: ActivityType): string {
  switch (type) {
    case 'athletics':
      return 'varsity_sport'
    case 'performing_arts':
      return 'performing_arts'
    case 'academic_team':
      return 'academic_competition'
    case 'student_organization':
      return 'student_organization'
    default:
      return 'academic_club'
  }
}

// ── Activity status ──────────────────────────────────────────────────────────

/** Seasonal programs move through a season; year-round clubs are simply active or not. */
export const SEASONAL_STATUSES = ['planning', 'registration_open', 'tryouts', 'active', 'postseason', 'completed', 'cancelled', 'archived'] as const
export const CLUB_STATUSES = ['active', 'inactive', 'archived'] as const
export type ActivityStatus = (typeof SEASONAL_STATUSES)[number] | (typeof CLUB_STATUSES)[number]

export const ACTIVITY_STATUS_LABELS: Record<ActivityStatus, string> = {
  planning: 'Planning',
  registration_open: 'Registration open',
  tryouts: 'Tryouts',
  active: 'Active',
  postseason: 'Postseason',
  completed: 'Completed',
  cancelled: 'Cancelled',
  archived: 'Archived',
  inactive: 'Inactive',
}

// ── Staff ────────────────────────────────────────────────────────────────────

export const ACTIVITY_STAFF_ROLES = [
  'head_coach',
  'assistant_coach',
  'advisor',
  'co_advisor',
  'volunteer',
  'athletic_trainer',
  'activity_director',
  'manager',
  'other',
] as const
export type ActivityStaffRole = (typeof ACTIVITY_STAFF_ROLES)[number]

export const ACTIVITY_STAFF_ROLE_LABELS: Record<ActivityStaffRole, string> = {
  head_coach: 'Head coach',
  assistant_coach: 'Assistant coach',
  advisor: 'Advisor',
  co_advisor: 'Co-advisor',
  volunteer: 'Volunteer',
  athletic_trainer: 'Athletic trainer',
  activity_director: 'Activity director',
  manager: 'Manager',
  other: 'Other',
}

export interface ActivityStaffAssignment {
  staffId: string
  role: ActivityStaffRole
}

// ── Configuration, on the activity document ─────────────────────────────────

/**
 * The fields the rebuild adds to an activity. All optional, so an activity
 * written before them still reads: its kind comes from its legacy type, its
 * status from `isActive`, its staff from advisor and assistants.
 */
export interface ActivityConfig {
  kind?: string
  status?: ActivityStatus
  schoolId?: string | null
  schoolYear?: string | null
  capacity?: number | null
  registrationOpensOn?: string | null
  registrationClosesOn?: string | null
  /** The rule set eligibility is evaluated against; default from the kind. */
  ruleSetKey?: string | null
  /** Form template keys a participant must have completed. */
  requiredFormKeys?: string[]
  tryoutsRequired?: boolean
  rosterApprovalRequired?: boolean
  transportationRequired?: boolean
  /** Shown in myEdu's Explore Activities. */
  visibleToFamilies?: boolean
  staff?: ActivityStaffAssignment[]
  /** Positions, sections or offices offered on the roster. */
  roles?: string[]
  /** What the roster calls a role: Position, Section, Office… */
  roleLabel?: string | null
  /** Sub-groups (Varsity / JV / Freshman; Marching / Concert band). */
  parentActivityId?: string | null
  numbered?: boolean
  competitive?: boolean
}

// ── Participation ───────────────────────────────────────────────────────────

/**
 * Interested or registered is not on the roster. A student is on the roster
 * only once selected or active.
 */
export const PARTICIPATION_STATUSES = [
  'interested',
  'registered',
  'pending_requirements',
  'tryout',
  'selected',
  'waitlisted',
  'active',
  'suspended',
  'withdrawn',
  'not_selected',
  'completed',
] as const
export type ParticipationStatus = (typeof PARTICIPATION_STATUSES)[number]

export const PARTICIPATION_LABELS: Record<ParticipationStatus, string> = {
  interested: 'Interested',
  registered: 'Registered',
  pending_requirements: 'Pending requirements',
  tryout: 'Tryout',
  selected: 'Selected',
  waitlisted: 'Waitlisted',
  active: 'Active',
  suspended: 'Suspended',
  withdrawn: 'Withdrawn',
  not_selected: 'Not selected',
  completed: 'Completed',
}

/** On the roster: counted against capacity and expected at events. */
export const ROSTER_STATUSES: readonly ParticipationStatus[] = ['selected', 'active', 'suspended']
/** Still in the pipeline toward the roster. */
export const PIPELINE_STATUSES: readonly ParticipationStatus[] = ['interested', 'registered', 'pending_requirements', 'tryout', 'waitlisted']

/** The status an older membership is read as. */
export function participationFromLegacy(status: string): ParticipationStatus {
  if ((PARTICIPATION_STATUSES as readonly string[]).includes(status)) return status as ParticipationStatus
  if (status === 'quit') return 'withdrawn'
  if (status === 'cut') return 'not_selected'
  if (status === 'ineligible') return 'active'
  return 'registered'
}

/** Moves a coach or the office may make, and what they need. */
const TRANSITIONS: Record<ParticipationStatus, readonly ParticipationStatus[]> = {
  interested: ['registered', 'withdrawn'],
  registered: ['pending_requirements', 'tryout', 'selected', 'waitlisted', 'active', 'withdrawn'],
  pending_requirements: ['registered', 'tryout', 'selected', 'waitlisted', 'active', 'withdrawn'],
  tryout: ['selected', 'waitlisted', 'not_selected', 'withdrawn'],
  selected: ['active', 'withdrawn'],
  waitlisted: ['selected', 'active', 'not_selected', 'withdrawn'],
  active: ['suspended', 'withdrawn', 'completed'],
  suspended: ['active', 'withdrawn'],
  withdrawn: ['registered'],
  not_selected: ['registered', 'waitlisted'],
  completed: [],
}

export function canMoveParticipation(from: ParticipationStatus, to: ParticipationStatus): boolean {
  return TRANSITIONS[from].includes(to)
}

/**
 * Capacity: a move onto the roster past the configured limit is refused
 * unless an override reason is given.
 */
export function capacityCheck(input: {
  capacity: number | null | undefined
  onRoster: number
  to: ParticipationStatus
  from: ParticipationStatus
}): { ok: true } | { ok: false; message: string } {
  if (!input.capacity || !ROSTER_STATUSES.includes(input.to) || ROSTER_STATUSES.includes(input.from)) return { ok: true }
  if (input.onRoster < input.capacity) return { ok: true }
  return { ok: false, message: `The roster is full (${input.onRoster} of ${input.capacity}). Waitlist the student, or give a reason to go over.` }
}

export interface ActivityParticipation extends BaseDocument {
  docType: 'activityParticipation'
  activityId: string
  studentId: string
  schoolId: string
  status: ParticipationStatus
  /** myEdu, the coach adding a student, a paper form, a transfer. */
  source: 'myedu' | 'staff' | 'paper' | 'transfer' | 'migrated'
  role?: string | null
  subgroup?: string | null
  number?: string | null
  waitlistPosition?: number | null
  registeredAt: string
  selectedAt?: string | null
  activatedAt?: string | null
  endedAt?: string | null
  /** The latest evaluated eligibility, kept with its history (never overwritten). */
  eligibility?: StoredEligibility | null
  eligibilityHistory?: { at: string; status: EligibilityStatus; summary: string }[]
  history: { at: string; from: ParticipationStatus | null; to: ParticipationStatus; byName: string; note?: string | null }[]
}

// ── Eligibility rules ───────────────────────────────────────────────────────

export type EligibilityStatus = 'eligible' | 'pending' | 'temporarily_ineligible' | 'ineligible' | 'restricted' | 'exception_review'

export const ELIGIBILITY_LABELS: Record<EligibilityStatus, string> = {
  eligible: 'Eligible',
  pending: 'Pending',
  temporarily_ineligible: 'Temporarily ineligible',
  ineligible: 'Ineligible',
  restricted: 'Restricted',
  exception_review: 'Exception under review',
}

export type EligibilityCategory = 'academic' | 'attendance' | 'physical' | 'health' | 'forms' | 'fees' | 'discipline' | 'enrollment' | 'grade_level'

export const ELIGIBILITY_CATEGORY_LABELS: Record<EligibilityCategory, string> = {
  academic: 'Academic',
  attendance: 'Attendance',
  physical: 'Physical',
  health: 'Health restriction',
  forms: 'Forms',
  fees: 'Fees',
  discipline: 'Activity restriction',
  enrollment: 'Enrollment',
  grade_level: 'Grade level',
}

/** One condition in a rule set. Each is optional and each is explained when it fails. */
export type EligibilityCondition =
  | { kind: 'academic'; minPassingCourses?: number; noFailingGrades?: boolean; minAveragePercent?: number; passingPercent?: number }
  | { kind: 'attendance'; windowDays: number; maxAbsentPercent: number }
  | { kind: 'physical' }
  | { kind: 'health_clearance' }
  | { kind: 'forms' }
  | { kind: 'fees' }
  | { kind: 'no_restriction' }
  | { kind: 'enrollment'; minCourses: number }
  | { kind: 'grade_level'; allowed: string[] }

export interface EligibilityRuleSet {
  key: string
  name: string
  description: string
  conditions: EligibilityCondition[]
  version: number
}

/** The rule sets a district starts with; Settings edits copies of these. */
export const DEFAULT_RULE_SETS: readonly EligibilityRuleSet[] = [
  {
    key: 'hs-athletics',
    name: 'High school athletics',
    description: 'Five passing courses this grading period, no more than 10% absences in 30 days, a current physical and clearance, forms, fees, and no activity restriction.',
    version: 1,
    conditions: [
      { kind: 'enrollment', minCourses: 5 },
      { kind: 'academic', minPassingCourses: 5, passingPercent: 60 },
      { kind: 'attendance', windowDays: 30, maxAbsentPercent: 10 },
      { kind: 'physical' },
      { kind: 'health_clearance' },
      { kind: 'forms' },
      { kind: 'fees' },
      { kind: 'no_restriction' },
    ],
  },
  {
    key: 'club-sport',
    name: 'Club sport',
    description: 'A current physical, forms, fees, and no activity restriction.',
    version: 1,
    conditions: [{ kind: 'physical' }, { kind: 'health_clearance' }, { kind: 'forms' }, { kind: 'fees' }, { kind: 'no_restriction' }],
  },
  {
    key: 'academic-only',
    name: 'Academic standing',
    description: 'No failing grades this grading period, forms, fees, and no activity restriction.',
    version: 1,
    conditions: [{ kind: 'academic', noFailingGrades: true, passingPercent: 60 }, { kind: 'forms' }, { kind: 'fees' }, { kind: 'no_restriction' }],
  },
  {
    key: 'honor-society',
    name: 'Honor society',
    description: 'An average of at least 90% this grading period and no activity restriction.',
    version: 1,
    conditions: [{ kind: 'academic', minAveragePercent: 90, passingPercent: 60 }, { kind: 'forms' }, { kind: 'no_restriction' }],
  },
  {
    key: 'participation',
    name: 'Participation only',
    description: 'Required forms and fees, and no activity restriction. No academic or physical rule.',
    version: 1,
    conditions: [{ kind: 'forms' }, { kind: 'fees' }, { kind: 'no_restriction' }],
  },
]

export const ruleSetFor = (key: string | null | undefined, custom: readonly EligibilityRuleSet[] = []): EligibilityRuleSet =>
  custom.find((set) => set.key === key) ?? DEFAULT_RULE_SETS.find((set) => set.key === key) ?? DEFAULT_RULE_SETS.find((set) => set.key === 'participation')!

/** Everything the engine needs, gathered by the caller from the modules that own it. */
export interface ActivityEligibilityFacts {
  today: string
  gradeLevel: string
  /** Current grades from the gradebook, one row per class. */
  courses: { name: string; percent: number | null }[]
  activeEnrollments: number
  attendance: { windowDays: number; daysRecorded: number; daysAbsent: number }
  /** Health Office status only — never a diagnosis. */
  physical: { status: 'current' | 'expiring' | 'expired' | 'missing'; expiresOn?: string | null }
  /** Health Office participation clearance: cleared, limited or not cleared, with dates. */
  clearance: { status: 'cleared' | 'limited' | 'not_cleared'; until?: string | null } | null
  /** Required forms the student has not completed. Labels only. */
  formsMissing: string[]
  formsRequired: number
  /** Whether the participation fee requirement is satisfied (paid or waived — never which). */
  fee: { required: boolean; satisfied: boolean; pending: boolean }
  /** Discipline's activity restriction covering today: dates and scope only. */
  restriction: { until?: string | null; source: 'discipline' | 'administration' } | null
  /** An approved administrator override, if one is in effect. */
  override: { reason: string; until?: string | null; waives: EligibilityCategory[] } | null
  /** A family's review request awaiting an administrator. */
  reviewRequested: boolean
}

export interface EligibilityReason {
  category: EligibilityCategory
  /** What a coach or advisor sees. */
  summary: string
  /** What someone with grade/attendance access sees; health and discipline never carry more than the summary. */
  detail: string
  /** Fails the status hard (ineligible) or softly (pending: something to finish). */
  severity: 'blocking' | 'pending'
  /** When the condition can next change, if known. */
  until?: string | null
}

export interface EligibilityEvaluation {
  status: EligibilityStatus
  reasons: EligibilityReason[]
  /** Conditions checked and passed, for "why is this student eligible?". */
  passed: EligibilityCategory[]
  ruleSetKey: string
  ruleSetVersion: number
  overridden: boolean
}

export interface StoredEligibility extends EligibilityEvaluation {
  evaluatedAt: string
  effectiveAt: string
}

/**
 * Evaluates one student against one rule set. Pure: the same facts always
 * give the same answer, and every non-eligible answer names its reasons.
 */
export function evaluateActivityEligibility(ruleSet: EligibilityRuleSet, facts: ActivityEligibilityFacts): EligibilityEvaluation {
  const reasons: EligibilityReason[] = []
  const passed: EligibilityCategory[] = []
  const fail = (reason: EligibilityReason) => reasons.push(reason)

  for (const condition of ruleSet.conditions) {
    switch (condition.kind) {
      case 'enrollment': {
        if (facts.activeEnrollments >= condition.minCourses) passed.push('enrollment')
        else
          fail({
            category: 'enrollment',
            summary: 'Enrollment: not enrolled in enough courses',
            detail: `Enrolled in ${facts.activeEnrollments} courses; ${condition.minCourses} are required.`,
            severity: 'blocking',
          })
        break
      }
      case 'academic': {
        const passingPercent = condition.passingPercent ?? 60
        const graded = facts.courses.filter((course) => course.percent != null)
        const failing = graded.filter((course) => (course.percent as number) < passingPercent)
        const passing = graded.length - failing.length
        const problems: string[] = []
        if (condition.minPassingCourses != null && graded.length > 0 && passing < condition.minPassingCourses) {
          problems.push(`${passing} of ${condition.minPassingCourses} required passing courses`)
        }
        if (condition.noFailingGrades && failing.length > 0) problems.push('a failing grade')
        if (condition.minAveragePercent != null && graded.length > 0) {
          const average = graded.reduce((sum, course) => sum + (course.percent as number), 0) / graded.length
          if (average < condition.minAveragePercent) problems.push(`an average of ${average.toFixed(1)}% (${condition.minAveragePercent}% required)`)
        }
        if (problems.length === 0) passed.push('academic')
        else
          fail({
            category: 'academic',
            summary:
              condition.minAveragePercent != null && problems.every((problem) => problem.startsWith('an average'))
                ? 'Academic: below the required average'
                : 'Academic: below the grade requirement',
            detail: [
              ...failing.map((course) => `${course.name}: ${(course.percent as number).toFixed(1)}%`),
              ...problems.map((problem) => problem.charAt(0).toUpperCase() + problem.slice(1)),
            ].join(' · '),
            severity: 'blocking',
          })
        break
      }
      case 'attendance': {
        const { daysRecorded, daysAbsent } = facts.attendance
        const percent = daysRecorded === 0 ? 0 : (daysAbsent / daysRecorded) * 100
        if (percent <= condition.maxAbsentPercent) passed.push('attendance')
        else
          fail({
            category: 'attendance',
            summary: 'Attendance: above the absence limit',
            detail: `Absent ${daysAbsent} of ${daysRecorded} school days in the last ${condition.windowDays} days (${percent.toFixed(0)}%; the limit is ${condition.maxAbsentPercent}%).`,
            severity: 'blocking',
          })
        break
      }
      case 'physical': {
        if (facts.physical.status === 'current' || facts.physical.status === 'expiring') passed.push('physical')
        else
          fail({
            category: 'physical',
            summary: facts.physical.status === 'expired' ? 'Physical expired' : 'Physical missing',
            detail: facts.physical.status === 'expired' ? `The physical on file expired ${facts.physical.expiresOn ?? ''}.`.replace(' .', '.') : 'No physical is on file with the Health Office.',
            severity: facts.physical.status === 'expired' ? 'blocking' : 'pending',
          })
        break
      }
      case 'health_clearance': {
        if (!facts.clearance || facts.clearance.status === 'cleared') passed.push('health')
        else
          fail({
            category: 'health',
            summary: facts.clearance.status === 'limited' ? 'Health restriction: limited participation' : 'Health restriction: not cleared',
            // A coach learns that, and until when — never why.
            detail: facts.clearance.until ? `Until ${facts.clearance.until}.` : 'Until the Health Office clears the student.',
            severity: facts.clearance.status === 'not_cleared' ? 'blocking' : 'pending',
            until: facts.clearance.until ?? null,
          })
        break
      }
      case 'forms': {
        if (facts.formsMissing.length === 0) passed.push('forms')
        else
          fail({
            category: 'forms',
            summary: `Forms: ${facts.formsRequired - facts.formsMissing.length} of ${facts.formsRequired} complete`,
            detail: `Missing: ${facts.formsMissing.join(', ')}.`,
            severity: 'pending',
          })
        break
      }
      case 'fees': {
        if (!facts.fee.required || facts.fee.satisfied) passed.push('fees')
        else
          fail({
            category: 'fees',
            summary: 'Fee requirement not yet satisfied',
            // Paid, waived or on a plan is the family's business; the coach only learns "satisfied".
            detail: facts.fee.pending ? 'The participation fee is pending.' : 'The participation fee is unpaid.',
            severity: 'pending',
          })
        break
      }
      case 'no_restriction': {
        if (!facts.restriction) passed.push('discipline')
        else
          fail({
            category: 'discipline',
            summary: 'Activity restriction',
            detail: facts.restriction.until ? `Restricted from activities until ${facts.restriction.until}.` : 'Restricted from activities.',
            severity: 'blocking',
            until: facts.restriction.until ?? null,
          })
        break
      }
      case 'grade_level': {
        if (condition.allowed.includes(facts.gradeLevel)) passed.push('grade_level')
        else
          fail({
            category: 'grade_level',
            summary: 'Grade level: not eligible for this activity',
            detail: `Grade ${facts.gradeLevel}; open to grades ${condition.allowed.join(', ')}.`,
            severity: 'blocking',
          })
        break
      }
    }
  }

  // An administrator's override waives named non-medical categories only.
  const MEDICAL: EligibilityCategory[] = ['physical', 'health']
  const waivable = facts.override ? facts.override.waives.filter((category) => !MEDICAL.includes(category)) : []
  const standing = reasons.filter((reason) => !waivable.includes(reason.category))
  const overridden = facts.override != null && standing.length < reasons.length

  let status: EligibilityStatus
  if (standing.some((reason) => reason.category === 'discipline')) status = 'restricted'
  else if (standing.some((reason) => reason.category === 'grade_level')) status = 'ineligible'
  else if (standing.some((reason) => reason.severity === 'blocking')) status = 'temporarily_ineligible'
  else if (standing.length > 0) status = 'pending'
  else status = 'eligible'
  if (facts.reviewRequested && status !== 'eligible') status = 'exception_review'

  return { status, reasons: standing, passed, ruleSetKey: ruleSet.key, ruleSetVersion: ruleSet.version, overridden }
}

/** A one-line summary for history: "Temporarily ineligible — Academic, Physical". */
export function eligibilitySummary(evaluation: Pick<EligibilityEvaluation, 'status' | 'reasons'>): string {
  const label = ELIGIBILITY_LABELS[evaluation.status]
  if (evaluation.reasons.length === 0) return label
  const categories = [...new Set(evaluation.reasons.map((reason) => ELIGIBILITY_CATEGORY_LABELS[reason.category]))]
  return `${label} — ${categories.join(', ')}`
}

/**
 * What a viewer may read of a reason. Coaches get the summary; people with
 * grade access get academic and attendance detail; health and discipline
 * reasons never carry more than their summary and dates, for anyone here.
 */
export function reasonForViewer(reason: EligibilityReason, access: { grades: boolean; attendance: boolean; finance: boolean }): { summary: string; detail: string | null } {
  if (reason.category === 'health' || reason.category === 'discipline' || reason.category === 'physical') {
    return { summary: reason.summary, detail: reason.until ? `Until ${reason.until}` : null }
  }
  if (reason.category === 'academic') return { summary: reason.summary, detail: access.grades ? reason.detail : null }
  if (reason.category === 'attendance') return { summary: reason.summary, detail: access.attendance ? reason.detail : null }
  if (reason.category === 'fees') return { summary: reason.summary, detail: access.finance ? reason.detail : null }
  return { summary: reason.summary, detail: reason.detail }
}

// ── Overrides, tryouts ───────────────────────────────────────────────────────

export interface ActivityEligibilityOverride extends BaseDocument {
  docType: 'activityEligibilityOverride'
  activityId: string
  studentId: string
  /** Non-medical categories only; physical and health clearance are never waivable here. */
  waives: EligibilityCategory[]
  reason: string
  startsOn: string
  endsOn?: string | null
  status: 'requested' | 'approved' | 'denied' | 'revoked'
  requestedByName: string
  decidedByName?: string | null
  decidedAt?: string | null
}

/** A family's request that an administrator review an eligibility decision. */
export interface ActivityEligibilityReview extends BaseDocument {
  docType: 'activityEligibilityReview'
  activityId: string
  studentId: string
  requestedByUserId: string
  requestedByName: string
  message: string
  status: 'open' | 'upheld' | 'overridden' | 'withdrawn'
  decidedByName?: string | null
  decidedAt?: string | null
  decisionNote?: string | null
}

export interface ActivityTryout extends BaseDocument {
  docType: 'activityTryout'
  activityId: string
  startsAt: string
  endsAt?: string | null
  location?: string | null
  staffIds: string[]
  /** Criteria the staff score against, if any; selection is always a human decision. */
  criteria: string[]
  evaluations: {
    studentId: string
    scores: Record<string, number | null>
    notes?: string | null
    decision?: 'selected' | 'waitlisted' | 'not_selected' | null
    subgroup?: string | null
  }[]
  status: 'scheduled' | 'evaluating' | 'decided'
}

/** A saved eligibility certification before a major event, preserved for audit. */
export interface ActivityEligibilitySnapshot extends BaseDocument {
  docType: 'activityEligibilitySnapshot'
  activityId: string
  label: string
  certifiedByName: string
  certifiedAt: string
  rows: { studentId: string; studentName: string; status: EligibilityStatus; summary: string }[]
}

/** District settings: rule sets edited from the defaults. */
export interface ActivitySettings extends BaseDocument {
  docType: 'activitySettings'
  ruleSets: EligibilityRuleSet[]
}
